/**
 * 카카오 로그인 state — 로그인을 시작한 브라우저와 돌아온 콜백을 대조한다 (Codex 검토 반영 2026-10-05).
 * - 시작 때 난수 생성 → localStorage 목록에 저장(30분 보관, 최대 5개 — 탭 여러 개 동시 로그인 허용) → 카카오 authorize 의 state 로 전달
 * - 콜백: 이 브라우저에 진행 중인 로그인이 있는데 돌아온 state 가 그중 어느 것과도 다르면 거절(남이 시작한 로그인 링크 혼입).
 *   맞으면 그 값만 지운다(1회용).
 * - 이 브라우저에 진행 중인 로그인이 하나도 없으면 통과 — 인스타·페이스북 앱 안에서 시작해 카카오톡을 거쳐
 *   사파리·크롬으로 돌아오는 경우 저장값이 없다. 이를 막으면 광고 유입 고객 로그인이 끊긴다(전환 우선, 독립 리뷰 지적).
 * localStorage 를 쓰는 이유: 카카오톡 앱 로그인 등으로 탭이 바뀌어도 같은 브라우저면 유지되게.
 */
const KEY = 'kakao_oauth_state';
const KEEP_MS = 30 * 60 * 1000;
const MAX_PENDING = 5;

type Pending = { v: string; exp: number };

function randomHex(bytes = 16): string {
  const a = new Uint8Array(bytes);
  const c = window.crypto || (window as any).msCrypto;
  if (c?.getRandomValues) c.getRandomValues(a);
  else for (let i = 0; i < bytes; i++) a[i] = Math.floor(Math.random() * 256); // crypto 없는 구형 브라우저 — 로그인은 막지 않는다
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

function readPending(): Pending[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
    const list: Pending[] = Array.isArray(raw) ? raw : raw && raw.v ? [raw] : [];
    const now = Date.now();
    return list.filter((p) => p && typeof p.v === 'string' && typeof p.exp === 'number' && p.exp > now);
  } catch {
    return [];
  }
}

function writePending(list: Pending[]) {
  try {
    if (list.length) localStorage.setItem(KEY, JSON.stringify(list.slice(-MAX_PENDING)));
    else localStorage.removeItem(KEY);
  } catch {
    /* 저장 불가 브라우저 — 콜백에서 '진행 중 로그인 없음'으로 통과 */
  }
}

export function createOAuthState(): string {
  const v = randomHex();
  writePending([...readPending(), { v, exp: Date.now() + KEEP_MS }]);
  return v;
}

/** 콜백 state 를 받아들일지. true = 진행. */
export function consumeOAuthState(received: string | null): boolean {
  const pending = readPending();
  if (pending.length === 0) return true; // 다른 브라우저로 돌아온 경우 등 — 기존처럼 로그인 진행
  const rest = pending.filter((p) => p.v !== received);
  if (!received || rest.length === pending.length) {
    writePending(pending); // 만료분만 정리, 진행 중 로그인은 유지
    return false;
  }
  writePending(rest);
  return true;
}
